import React from "react";
import { motion } from "framer-motion";
import { Lock, Truck, CreditCard, ShieldCheck, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface CartSummaryProps {
  totalPrice: number;
  onCheckout: () => void;
  loading?: boolean;
  minimumOrderAmount?: number;
  totalPriceIncludeVat: number;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  totalPrice,
  onCheckout,
  loading = false,
  totalPriceIncludeVat,
  minimumOrderAmount = 0,
}) => {
  const formattedAmount = totalPriceIncludeVat.toLocaleString("en-ZA", {
    style: "currency",
    currency: "ZAR",
  });

  const isCheckoutAllowed = totalPrice >= minimumOrderAmount;
  // const qualifiesForShippingDiscount = totalPrice >= 400;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-[#75A7AD] rounded-2xl p-6 shadow-md">
      {/* Order Summary */}
      <h3 className="text-lg font-bold text-white mb-4">Order Summary</h3>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-white/90">
          <span>Subtotal (incl. VAT)</span>
          <span className="font-medium">{formattedAmount}</span>
        </div>

        <div className="flex items-center justify-between text-white/90">
          <div className="flex items-center gap-1.5">
            <Truck size={16} className="text-white" />
            <span>Shipping</span>
          </div>
          {/* <span className="font-medium text-white">Calculated at checkout</span> */}
          <span className="font-medium text-white">FREE</span>
        </div>
      </div>

      <div className="flex items-center justify-between py-3 border-t border-b border-white/20 mb-4">
        <span className="text-lg font-bold text-white">Total (incl. VAT)</span>
        <span className="text-xl font-bold text-white">{formattedAmount}</span>
      </div>

      {isCheckoutAllowed ? (
        <button
          onClick={onCheckout}
          disabled={loading}
          className="w-full bg-white text-[#75A7AD] rounded-xl py-3 px-6 font-semibold 
                  shadow-md transition-all duration-200 
                  hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed mb-3">
          <span className="flex items-center justify-center gap-2">
            {loading ? (
              <div className="w-5 h-5 border-2 border-[#75A7AD] border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Lock size={18} />
                Secure Checkout
              </>
            )}
          </span>
        </button>
      ) : (
        <div className="space-y-3 mb-3">
          <button
            disabled
            className="w-full bg-white/50 text-[#75A7AD]/70 rounded-xl py-3 px-6 font-semibold 
                    shadow-sm cursor-not-allowed">
            <span className="flex items-center justify-center gap-2">
              <Lock size={18} />
              Secure Checkout
            </span>
          </button>

          <div className="flex items-center gap-2 text-white bg-white/10 p-2 rounded-lg border border-white/20 text-sm">
            Minimum order:{" "}
            {minimumOrderAmount.toLocaleString("en-ZA", {
              style: "currency",
              currency: "ZAR",
            })}
          </div>
        </div>
      )}

      <Link to="/flavours">
        <button
          className="w-full bg-white/20 text-white border border-white/30 rounded-xl py-3 px-6 font-medium 
                 shadow-sm transition-all duration-200 hover:bg-white/30">
          <span className="flex items-center justify-center gap-2">
            <ArrowLeft size={18} />
            Return to Shopping
          </span>
        </button>
      </Link>

      <div className="mt-4 pt-3 border-t border-white/20 flex justify-center">
        <div className="flex items-center gap-2 text-white/80 text-sm">
          <ShieldCheck size={16} className="text-white" />
          <span>Secure Checkout</span>
          <CreditCard size={14} className="ml-2" />
        </div>
      </div>
    </motion.div>
  );
};

export default CartSummary;
